import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  convertDataToForm,
  getUniqueColumnValues,
} from '../../filters/filters.helper';
import { initialValues } from '../../filters/filters.config';
import DataTable from '../../../../components/shared/DataTable/DataTable';
import { COLUMNS, FILTERS_CONFIG, SEARCH_CONFIG } from './InvoiceList.config';
import { Button } from 'primereact/button';
import { TabPanel, TabView } from 'primereact/tabview';
import cn from 'classnames';

import './InvoicesList.css';
import { Divider } from 'primereact/divider';
import { showToast } from '../../../../helpers/showToast';
import { getAssetUrl } from '../../../../services/apis';

const INVOICE_TYPE_TABS = ['SALES', 'PURCHASES', 'THIRD_PARTY'];

const InvoicesList = ({
  renderedInvoices,
  originalInvoices,
  setInvoicesList,
  tableLoading,
  invoiceType,
  setInvoiceType,
  lastUpdated,
  onRefresh,
  handleSubmitFilters,
  onReset,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const { t } = useTranslation();
  const tableRef = useRef();

  const handleReset = (resetForm) => {
    resetForm({ values: initialValues });
    onReset();
  };

  const handleDownloadManual = async () => {
    setIsDownloading(true);
    try {
      const response = await getAssetUrl('raseed-user-manual-clean');
      const { url } = response.data;

      if (url) {
        window.open(url, '_blank');
      }
    } catch (error) {
      showToast({
        data: {
          type: 'error',
          message: t('failed_to_download_file'),
        },
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="invoice-list mt-3">
      {/* Header Section */}
      <div className="invoice-list-header mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h1 className="invoice-list-title mb-2">
              {t('invoice_list.title')}
            </h1>
            {lastUpdated && (
              <div className="last-updated d-flex align-items-center gap-2">
                <span className="text-muted">
                  {t('common.last_updated')}:{' '}
                  {lastUpdated.format('D MMM YYYY, HH:mm')}
                </span>
                <Button
                  icon="pi pi-refresh"
                  rounded
                  text
                  severity="secondary"
                  onClick={onRefresh}
                  loading={tableLoading}
                  className="p-button-sm"
                  tooltip={t('common.refresh')}
                />
              </div>
            )}
            {renderedInvoices?.length === 100 && (
              <p className={cn('d-flex gap-2 my-3 exceed-hundered')}>
                <i className="pi icn-info-circle-filled color-theme-primary mr-3" />
                {t('invoice_view_page.exceed_hundred')}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="table-container">
        {/* Data Table */}
        <DataTable
          ref={tableRef}
          showAdvancedFilters
          showAdvancedFiltersChips={false}
          filterDisplay="menu"
          showSortInFilters={true}
          exportFilename="invoice-list"
          beforeFilters={({ ref }) => (
            <>
              <Button
                className="text-nowrap px-4"
                label={t('common.export_list')}
                onClick={() => {
                  ref?.current?.exportCSV();
                }}
              />
              <Divider layout="vertical" align="center" />
            </>
          )}
          afterFilters={() => (
            <TabView
              activeIndex={INVOICE_TYPE_TABS.indexOf(invoiceType)}
              onTabChange={(e) => {
                setInvoiceType(INVOICE_TYPE_TABS[e.index]);
              }}
            >
              <TabPanel header={t('invoice_view_page.dashboard.sales')} />
              <TabPanel header={t('invoice_view_page.dashboard.purchases')} />
              <TabPanel header={t('invoice_view_page.dashboard.third-party')} />
            </TabView>
          )}
          loading={tableLoading}
          value={renderedInvoices}
          columns={() => COLUMNS({ t, invoiceType })}
          searchConfig={SEARCH_CONFIG}
          dataKey="uuid"
          filters={getUniqueColumnValues(
            originalInvoices,
            FILTERS_CONFIG.filtersValues,
            t,
          )}
          filtersConfig={FILTERS_CONFIG}
          onResetFilters={handleReset}
          onSubmitFilters={handleSubmitFilters}
          convertDataToForm={convertDataToForm}
        />
        <p className="guide-help">
          {t('invoice_view_page.list_guide')}{' '}
          <Button
            className="p-0 px-1"
            link
            onClick={handleDownloadManual}
            disabled={isDownloading}
          >
            {t('common.here')}
          </Button>
        </p>
      </div>
    </div>
  );
};

export default InvoicesList;
